import * as React from 'react';
import { styled } from '@mui/system';
import colors from '../../../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import ActionButton from 'Components/ActionButton';
import { Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps } from '@mui/material';
import { dialogActions, dialogContent, dialogTitle } from '../../../../../config/theme/componentTheme';
import MMSEForm from '../MMSE';
import { actions, TCreationPromiseType } from 'Store/documents/actions';
import { actions as actionsForm } from 'Store/form';
import { useAppDispatch } from 'Store/index';
import { sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { mmseTotalScore } from 'Routes/Cognition/utils';
import { TDocument } from '../../definitions';

const StyledButton = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
  textTransform: 'uppercase',
});

const DialogPaper = (props: PaperProps): React.JSX.Element => <Paper square={true} {...props} />;

const MMSEDialog = ({ documents = [], formData, viewing, fm }: IOwnProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [editingData, setEditingData] = React.useState<Omit<IMMSE, keyof IControlProps>>({});
  const dispatch = useAppDispatch();

  const isViewing = !!viewing;

  const latestMmse: IMMSE | undefined = documents
    .toSorted((a, b) => b._cdate - a._cdate)
    .toSorted((a, b) => sortPartialDate(b.date, a.date))
    .find(d => d._type === 'mmse' && sortPartialDate(d.date, formData.document.date) <= 0) as IMMSE | undefined;

  // Create new mmse document
  const addNewMmse = (): void => {
    const fetchID = async (): Promise<TCreationPromiseType> => actions.createDocument('mmse', true)(dispatch);
    fetchID()
      .then((id: TCreationPromiseType) => {
        if (typeof id === 'object' && id._id) {
          actions.closeCommit(
            { id: id._id as string, name: 'mmse' },
            editingData as TAnyObject & IControlProps,
          )(dispatch);
          dispatch(actionsForm.unsetEditing(id._id as string));
        }
      })
      .catch(() => {
        return;
      });
  };

  return (
    <React.Fragment>
      <Container justifyContent="flex-end" alignItems="center">
        <Item xs={8}>
          {!latestMmse ? (
            fm('cognition.cerad.mmseNotFilled')
          ) : (
            <Container>
              <Item style={{ fontWeight: 600 }}>
                {latestMmse.unableToPerform
                  ? fm('cognition.unableToPerform')
                  : latestMmse.totalScoreOnly?.[0]
                    ? latestMmse.totalScore
                    : mmseTotalScore(latestMmse)}
              </Item>
              <Item style={{ padding: '0 1rem' }}>{` (${formatPartialDate(latestMmse.date)})`}</Item>
            </Container>
          )}
        </Item>
        <Item xs={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!isViewing && (
            <StyledButton style={{ textTransform: 'none', paddingRight: '1rem' }} onClick={() => setDialogOpen(true)}>
              {`+ ${fm('general.new')}`}
            </StyledButton>
          )}
        </Item>
      </Container>
      <Dialog open={dialogOpen} maxWidth="lg" fullWidth={true} PaperComponent={DialogPaper}>
        <DialogTitle style={dialogTitle}>{fm('cognition.mmse.mmse')}</DialogTitle>
        <DialogContent style={{ ...dialogContent, overflowX: 'hidden' }}>
          <MMSEForm
            formData={{
              onChange: (values: TOnChangeValues) => {
                setEditingData({ ...editingData, ...values });
              },
              document: editingData as IMMSE,
            }}
            viewing={false}
          />
        </DialogContent>
        <DialogActions style={dialogActions}>
          <StyledButton style={{ marginRight: '2rem' }} onClick={() => setDialogOpen(false)}>
            {fm('general.cancel')}
          </StyledButton>
          <ActionButton
            text="general.save"
            width={10}
            height={3}
            fontSize={16}
            onClick={() => {
              addNewMmse();
              setDialogOpen(false);
            }}
          />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<TDocument>;
  formData: IFormData<ICERAD2023>;
  viewing?: boolean;
  fm: (id: string, values?: Record<string, (chunks: string[]) => React.JSX.Element> | undefined) => React.ReactNode[];
}

export default MMSEDialog;
